import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader, Card, StatsCard, ActionButton } from "../shared";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import { MobileOnlyAdBanner } from "./masterOrgAdUtils";

const MasterOrgAdvertisementAnalytics = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { id } = useParams();
  const token = props?.props?.profile?.token;

  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [period, setPeriod] = useState("30d");

  const headers = () => ({
    "Content-Type": "application/json",
    "x-access-token": token,
  });

  useEffect(() => {
    if (token) fetchAnalytics();
  }, [token, period, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const body = { period };
      if (id) body.id = Number(id);
      const res = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ADVERTISEMENT_ANALYTICS,
        JSON.stringify(body),
        headers()
      );
      if (res?.status) {
        setAnalytics(res.data || null);
      } else {
        toast.error(res?.message || "Failed to load analytics");
      }
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const summary = (() => {
    if (!analytics?.summary?.length) return { impressions: 0, clicks: 0, ctr: 0 };
    if (id) {
      const row = analytics.summary[0];
      return {
        impressions: row.total_impressions || 0,
        clicks: row.total_clicks || 0,
        ctr: row.ctr != null ? Number(row.ctr).toFixed(1) : "0.0",
      };
    }
    const totals = analytics.summary.reduce(
      (acc, row) => ({
        impressions: acc.impressions + (row.total_impressions || 0),
        clicks: acc.clicks + (row.total_clicks || 0),
      }),
      { impressions: 0, clicks: 0 }
    );
    const ctr = totals.impressions > 0
      ? ((totals.clicks / totals.impressions) * 100).toFixed(1)
      : "0.0";
    return { ...totals, ctr };
  })();

  const axisColor = isDark ? "#cbd5e1" : "#718096";
  const tooltipStyle = {
    backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
    border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e2e8f0",
    borderRadius: "8px",
    color: isDark ? "#ffffff" : "#1a202c",
  };

  const tableHeadSx = {
    backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
  };
  const cellSx = {
    borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
    fontSize: "0.875rem",
    color: isDark ? "#e2e8f0" : "#334155",
  };
  const headCellSx = {
    ...cellSx,
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: isDark ? "#94a3b8" : "#64748b",
  };

  return (
    <Box>
      <PageHeader
        title={id ? "Ad Analytics" : "Advertisement Analytics"}
        subtitle={
          id
            ? "Mobile app impressions and taps for this advertisement"
            : "Mobile app impressions and taps across all your ads"
        }
        breadcrumbs={[
          { label: "Club Partner" },
          { label: "Advertisements", onClick: () => navigate("/master-org/advertisements") },
          { label: "Analytics" },
        ]}
      >
        <ActionButton
          variant="outline"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/master-org/advertisements")}
        >
          Back
        </ActionButton>
      </PageHeader>

      <MobileOnlyAdBanner />

      {/* Period selector */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, val) => { if (val) setPeriod(val); }}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              px: 3,
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "#e2e8f0",
              color: isDark ? "#94a3b8" : "#64748b",
              "&.Mui-selected": {
                backgroundColor: isDark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.1)",
                color: isDark ? "#a5b4fc" : "#6366f1",
                borderColor: isDark ? "#6366f1" : "#6366f1",
              },
            },
          }}
        >
          <ToggleButton value="7d">Last 7 days</ToggleButton>
          <ToggleButton value="30d">Last 30 days</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={36} />
        </Box>
      ) : (
        <>
          {/* Summary stats */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <StatsCard
                title="App Impressions"
                value={summary.impressions.toLocaleString()}
                icon={VisibilityIcon}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatsCard
                title="App Taps"
                value={summary.clicks.toLocaleString()}
                icon={TouchAppIcon}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatsCard
                title="Click-Through Rate"
                value={`${summary.ctr}%`}
                icon={ShowChartIcon}
                color="info"
              />
            </Grid>
          </Grid>

          {/* Daily trend chart */}
          <Card title="Daily Trend (mobile app)" sx={{ mb: 3 }}>
            {analytics?.daily_trend?.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart
                  data={analytics.daily_trend}
                  margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}
                  />
                  <XAxis
                    dataKey="date"
                    stroke={axisColor}
                    tick={{ fill: axisColor, fontSize: "0.72rem" }}
                  />
                  <YAxis
                    stroke={axisColor}
                    tick={{ fill: axisColor, fontSize: "0.72rem" }}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="impressions"
                    stroke="#6366f1"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="clicks"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <Typography sx={{ color: isDark ? "#64748b" : "#94a3b8", fontSize: "0.875rem" }}>
                  No trend data available
                </Typography>
              </Box>
            )}
          </Card>

          {/* Role breakdown */}
          <Card title="Role Breakdown (mobile app)">
            {analytics?.role_breakdown?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={tableHeadSx}>
                    <TableRow>
                      <TableCell sx={headCellSx}>Role</TableCell>
                      <TableCell sx={headCellSx} align="right">Impressions</TableCell>
                      <TableCell sx={headCellSx} align="right">Clicks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.role_breakdown.map((row) => (
                      <TableRow key={row.role}>
                        <TableCell sx={cellSx}>
                          <Typography sx={{ textTransform: "capitalize", fontSize: "0.875rem" }}>
                            {row.role}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          {(row.impressions || 0).toLocaleString()}
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          {(row.clicks || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <Typography sx={{ color: isDark ? "#64748b" : "#94a3b8", fontSize: "0.875rem" }}>
                  No role data available
                </Typography>
              </Box>
            )}
          </Card>

        </>
      )}
    </Box>
  );
};

export default MasterOrgAdvertisementAnalytics;
